import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Chip, CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { formatDisplayDate } from "utils/dateFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaPrint } from "react-icons/fa";
import { IoArrowBack } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { getFileIcon, getOriginalFileName } from "utils/utils";
import InvoiceTemplate from "./InvoiceTemplate";
import { collectInvoiceReceipts } from "./invoiceReceipts";
import InvoicePrintThemeToggle from "components/Invoice/InvoicePrintThemeToggle";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import LinkedInvoiceView from "components/ModuleInvoice/LinkedInvoiceView";
import ModuleInvoiceDialog from "components/ModuleInvoice/ModuleInvoiceDialog";
import { isModuleInvoice } from "components/ModuleInvoice/moduleLinks";
import RecordModulePaymentDialog from "components/ModulePayments/RecordModulePaymentDialog";

const InvoiceStatusLoader = ({ onBack }) => (
  <div className="flex flex-wrap mt-4 dashBody">
    <div className="w-full mb-12 px-4">
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
        <div className="rounded-t mb-0 px-10 py-3 border-0">
          <div className="flex justify-between items-center">
            <div className="relative flex gap-4 items-center">
              <IoArrowBack className="text-xl cursor-pointer" onClick={onBack} />
              <h3 className="font-semibold text-xl text-app-text">Loading Invoice</h3>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[420px] gap-4">
          <CircularProgress size={36} />
          <div className="text-center">
            <p className="font-semibold text-app-text">Loading invoice</p>
            <p className="text-sm text-app-text-muted">
              Please wait while we load the invoice details.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const safeNum = (v) => Number(v || 0);

const computeTotals = (invoices = []) => {
  const subtotal = invoices.reduce((sum, i) => sum + safeNum(i?.price) * safeNum(i?.quantity), 0);
  const gst = invoices
    .filter((i) => i?.gst === "yes" || i?.gst === "true" || i?.gst === true)
    .reduce((sum, i) => sum + safeNum(i?.price) * safeNum(i?.quantity) * 0.1, 0);
  return { subtotal, gst, total: subtotal + gst };
};

const isImagePath = (filePath) => {
  if (!filePath) return false;
  const ext = filePath.split("?")[0].split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
};

const ReceiptCard = ({ url }) => {
  const isImage = isImagePath(url);
  const name = getOriginalFileName(url);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-lg border border-app-border bg-app-surface hover:shadow-md transition overflow-hidden"
    >
      <div className="h-40 bg-app-surface-muted flex items-center justify-center">
        {isImage ? (
          <img src={url} alt={name} className="max-h-40 max-w-full object-contain" />
        ) : (
          <div className="text-5xl">{getFileIcon(url)}</div>
        )}
      </div>
      <div className="flex items-center gap-2 px-3 py-2 border-t border-app-border">
        <span className="text-lg shrink-0">{getFileIcon(url)}</span>
        <span className="text-sm text-app-text truncate flex-1" title={name}>
          {name}
        </span>
        <OpenInNewIcon fontSize="small" className="text-app-text-muted group-hover:text-primary" />
      </div>
    </a>
  );
};

const MetaCard = ({ label, value }) => (
  <div className="bg-app-surface border border-app-border rounded-lg px-4 py-3">
    <p className="text-xs uppercase font-semibold tracking-wide text-app-text-muted">{label}</p>
    <p className="text-base font-semibold text-app-text mt-1">{value}</p>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <div className="bg-app-surface border border-app-border rounded-lg p-5">
    <div className="mb-4">
      <h3 className="text-sm font-bold text-app-text uppercase tracking-wide">{title}</h3>
      {subtitle && <p className="text-xs text-app-text-muted mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const PaidInvoiceView = ({ data }) => {
  const { subtotal, gst, total } = useMemo(() => computeTotals(data?.invoices), [data?.invoices]);
  const receipts = collectInvoiceReceipts(data);

  const fmt = (val) => `$${safeNum(val).toFixed(2)}`;
  const fmtDate = (v) => formatDisplayDate(v, "—");

  return (
    <div className="px-6 md:px-10 py-8 space-y-6">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center text-2xl font-bold">
            ✓
          </div>
          <div>
            <p className="text-emerald-700 font-semibold text-lg">Payment Received</p>
            <p className="text-sm text-emerald-700/80">This invoice has been marked as paid.</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase font-semibold text-emerald-700/80">Amount Paid</p>
          <p className="text-3xl font-bold text-emerald-700">{fmt(total)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetaCard label="Paid Date" value={fmtDate(data?.paidDate)} />
        <MetaCard label="Payment Method" value={data?.paymentMethod || "—"} />
        <MetaCard label="Issue Date" value={fmtDate(data?.issueDate)} />
        <MetaCard label="Due Date" value={fmtDate(data?.dueDate)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="From">
          <p className="font-semibold text-app-text">{data?.companyName}</p>
          {data?.location && <p className="text-sm text-app-text-secondary">{data.location}</p>}
          {data?.invoiceFromAbn && (
            <p className="text-sm text-app-text-secondary">ABN: {data.invoiceFromAbn}</p>
          )}
          {data?.email && <p className="text-sm text-app-text-secondary">{data.email}</p>}
          {data?.contact && <p className="text-sm text-app-text-secondary">{data.contact}</p>}
        </Panel>
        <Panel title="Bill To">
          <p className="font-semibold text-app-text">{data?.invoiceTo || "—"}</p>
          {data?.address && <p className="text-sm text-app-text-secondary">{data.address}</p>}
          {data?.contactNumber && (
            <p className="text-sm text-app-text-secondary">{data.contactNumber}</p>
          )}
          {data?.abn && <p className="text-sm text-app-text-secondary">ABN: {data.abn}</p>}
        </Panel>
      </div>

      <Panel title="Line Items">
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-app-surface-muted border-b border-app-border">
                <th className="text-left py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs">
                  Description
                </th>
                <th className="text-center py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-24">
                  Qty
                </th>
                <th className="text-right py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-28">
                  Unit Price
                </th>
                <th className="text-center py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-20">
                  GST
                </th>
                <th className="text-right py-2.5 px-4 font-semibold text-app-text-secondary uppercase text-xs w-32">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.invoices?.length ? (
                data.invoices.map((item, idx) => (
                  <tr key={item?.id || item?.uid || idx} className="border-b border-app-border">
                    <td className="py-2.5 px-4 text-app-text">{item?.description || "—"}</td>
                    <td className="py-2.5 px-4 text-center text-app-text">
                      {item?.quantity ?? "—"}
                    </td>
                    <td className="py-2.5 px-4 text-right text-app-text">{fmt(item?.price)}</td>
                    <td className="py-2.5 px-4 text-center text-app-text">
                      {item?.gst === "yes" || item?.gst === "true" || item?.gst === true
                        ? "10%"
                        : "—"}
                    </td>
                    <td className="py-2.5 px-4 text-right font-semibold text-app-text">
                      {fmt(safeNum(item?.price) * safeNum(item?.quantity))}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-app-text-muted">
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mt-4">
          <div className="w-full md:w-80 space-y-2">
            <div className="flex justify-between text-sm text-app-text-secondary">
              <span>Subtotal</span>
              <span className="font-semibold">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-app-text-secondary">
              <span>GST (10%)</span>
              <span className="font-semibold">{fmt(gst)}</span>
            </div>
            <div className="flex justify-between text-base font-bold pt-2 border-t-2 border-app-border text-app-text">
              <span>Total AUD</span>
              <span>{fmt(total)}</span>
            </div>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Payment info" subtitle="As shared on the invoice">
          <div className="text-sm text-app-text space-y-1">
            <p>
              <span className="text-app-text-muted">Account name:</span>{" "}
              {data?.paymentInfo?.accountName || "—"}
            </p>
            <p>
              <span className="text-app-text-muted">BSB:</span>{" "}
              {data?.paymentInfo?.bankDetails || "—"}
            </p>
            <p>
              <span className="text-app-text-muted">Account number:</span>{" "}
              {data?.paymentInfo?.accountNumber || "—"}
            </p>
          </div>
        </Panel>

        <Panel title="Receipts" subtitle="Payment proof / uploaded receipts">
          {receipts.length === 0 ? (
            <p className="text-sm text-app-text-muted">No receipts uploaded.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {receipts.map((url) => (
                <ReceiptCard key={url} url={url} />
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

const ViewInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const contentRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const reactToPrintFn = useReactToPrint({
    contentRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });
  const [data, setData] = useState(null);
  const [moduleDialog, setModuleDialog] = useState(null);

  const { mutate: getInvoiceData, isLoading } = useMutation(
    async (invoiceId) => {
      const response = await axios.get(`${API_URL}/invoice/${invoiceId}`);
      return response.data.data;
    },
    {
      onSuccess: (invoice) => setData(invoice),
      onError: () => toast.error("Error loading invoice"),
    }
  );

  useEffect(() => {
    if (id) getInvoiceData(id);
  }, [id, getInvoiceData]);

  if (isLoading || !data) {
    return <InvoiceStatusLoader onBack={() => navigate(-1)} />;
  }

  if (data?.status !== true && !isModuleInvoice(data)) {
    navigate(`/admin/invoice/edit/${data?.invoiceId || id}?edit=1`, { replace: true });
    return null;
  }

  const linked = isModuleInvoice(data);

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-10 py-3 border-0">
            <div className="flex justify-between items-center">
              <div className="relative flex gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className="font-semibold text-xl text-app-text">View Invoice</h3>
                {data?.status === true && <Chip label="PAID" color="success" size="small" />}
              </div>
              <div className="flex gap-3 items-center">
                <InvoicePrintThemeToggle />
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() =>
                    linked
                      ? setModuleDialog("edit")
                      : navigate(`/admin/invoice/edit/${data?.invoiceId || id}?edit=1`)
                  }
                >
                  Edit
                </Button>
                {linked && (
                  <Button variant="outlined" onClick={() => setModuleDialog("payment")}>
                    Record payment
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={() => reactToPrintFn()}
                  startIcon={<FaPrint />}
                >
                  Print Invoice
                </Button>
              </div>
            </div>
          </div>

          {linked ? <LinkedInvoiceView invoice={data} /> : <PaidInvoiceView data={data} />}
        </div>
      </div>

      {contentRef && <InvoiceTemplate printData={data} contentRef={contentRef} />}
      {linked && (
        <>
          <ModuleInvoiceDialog
            open={moduleDialog === "edit"}
            mode="edit"
            moduleType={data.moduleType}
            moduleId={data.moduleId}
            clientId={data.clientId}
            onClose={() => setModuleDialog(null)}
            onSaved={() => getInvoiceData(id)}
          />
          <RecordModulePaymentDialog
            open={moduleDialog === "payment"}
            moduleType={data.moduleType}
            moduleId={data.moduleId}
            clientId={data.clientId}
            onClose={() => setModuleDialog(null)}
            onRecorded={() => getInvoiceData(id)}
          />
        </>
      )}
    </div>
  );
};

export default ViewInvoice;
